import type {EmitterSubscription} from 'react-native'
import * as Braze from '@braze/web-sdk'

import {getValidBrazeContentCards} from 'src/lib/braze/braze.utils'
import Log from 'src/lib/loggingUtil'
import {BrazeContentCard, BrazeInterface} from 'src/lib/braze/braze.types'
import {
  mapWebContentCardsToCrossPlatformContentCards,
  WebContentCard,
} from 'src/lib/braze/braze.web.utils'

let hasAttemptedInit = false
function initBraze(key: string): void {
  if (!hasAttemptedInit) {
    hasAttemptedInit = true
    const wasInitSuccessful = Braze.initialize(key, {
      baseUrl: 'sdk.iad-03.braze.com',
    })

    if (!wasInitSuccessful) {
      Log.error('Braze SDK failed to initialize')
    } else {
      Braze.openSession()
    }
  }
}

/**
 * Exposes a normalized API for subscribing to Braze Content Card across all platforms.
 */
function subscribeToBrazeContentCards(
  callback: (cards: BrazeContentCard[]) => void,
): EmitterSubscription['remove'] {
  const contentCardSubscription = Braze.subscribeToContentCardsUpdates((data) => {
    /* Filters out any Braze Content Cards that do no have our required schema
     * (`screen`,`location`) and performs a type conversion on the SDK's string responses to return
     * a type safe array of `BrazeContentCard` objects. */
    try {
      const validContentCards = getValidBrazeContentCards(
        //The web SDK returns a different schema than `@braze/react-native-sdk`.
        mapWebContentCardsToCrossPlatformContentCards(data.cards),
      )

      callback(validContentCards)
    } catch (error) {
      Log.error(error, `Error getting valid Braze Content Cards: ${JSON.stringify(data)}`)
    }
  })

  return () => {
    if (contentCardSubscription) {
      Braze.removeSubscription(contentCardSubscription)
    }
  }
}

/**
 * Exposes a normalized API for tracking Braze Content Card clicks via the Braze SDK across
 * all platforms.
 */
function logContentCardClick(datum: BrazeContentCard): void {
  /* It's unclear why but `Braze.logContentCardClick` won't log a click unless a `url` property
   * is present. */
  datum.url = datum.url ?? 'boom' // 💥

  Braze.logContentCardClick(mapBrazeContentCardToWebSchema(datum))
}

/**
 * Exposes a normalized API for tracking Braze Content Card dismiss events via the Braze SDK across
 * all platforms.
 */
function logContentCardDismissal(datum: BrazeContentCard): void {
  /* It's unclear why but `Braze.logContentCardClick` won't log a click unless a `url` property
   * is present. */
  datum.url = datum.url ?? 'boom' // 💥

  Braze.logCardDismissal(mapBrazeContentCardToWebSchema(datum))
}

/**
 * Maps our type safe `BrazeContentCard` back to the `@braze/web-sdk` compatible class.
 */
export function mapBrazeContentCardToWebSchema(datum: BrazeContentCard): WebContentCard {
  /* Unfortunately our normalized `BrazeContentCard` has to be converted back to a `WebContentCard`.
   * for use with this method of the Braze Web SDK. */

  switch (datum.type) {
    case 'ImageOnly':
      return Object.assign(new Braze.ImageOnly(), {
        ...datum,
        aspectRatio: datum.imageAspectRatio,
        created: new Date(datum.created),
        expiresAt: new Date(datum.expiresAt),
        imageUrl: datum.image,
      })

    case 'Captioned':
      return Object.assign(new Braze.CaptionedImage(), {
        ...datum,
        aspectRatio: datum.imageAspectRatio,
        created: new Date(datum.created),
        description: datum.cardDescription,
        expiresAt: new Date(datum.expiresAt),
        imageUrl: datum.image,
      })
    case 'Classic':
      return Object.assign(new Braze.ClassicCard(), {
        ...datum,
        created: new Date(datum.created),
        description: datum.cardDescription,
        expiresAt: new Date(datum.expiresAt),
        imageUrl: datum?.image,
      })
  }
}

/**
 * Exposes a normalized API for requesting Braze Content Card updates via the Braze SDK across
 * all platforms.
 */
function requestContentCardsRefresh(): void {
  Braze.requestContentCardsRefresh()
}

function changeUser(userId: string): void {
  Braze.changeUser(userId)
}

function logCustomEvent(eventName: string, eventProperties?: object): void {
  Braze.logCustomEvent(eventName, eventProperties)
}

function setCustomUserAttribute(
  key: string,
  value: number | boolean | string | string[] | Date | null | object | object[],
): void {
  Braze.getUser?.()?.setCustomUserAttribute(key, value)
}

const PfBraze: BrazeInterface = {
  logContentCardClick,
  logContentCardDismissal,
  subscribeToBrazeContentCards,
  requestContentCardsRefresh,
  initBraze,
  logCustomEvent,
  changeUser,
  setCustomUserAttribute,
}

// eslint-disable-next-line import/no-default-export
export default PfBraze
